<template>
  <div>
    <b-alert variant="warning" show class="mb-1" v-if="unresolvedCount">
      <div class="alert-body">
        <strong>{{ unresolvedCount }}</strong> item{{ unresolvedCount === 1 ? "" : "s" }}
        still need a decision before this import can run.
      </div>
    </b-alert>
    <b-alert variant="success" show class="mb-1" v-else>
      <div class="alert-body">
        Everything is mapped. Review the choices below, then confirm.
      </div>
    </b-alert>

    <b-alert
      variant="danger"
      show
      class="mb-1"
      v-for="(w, i) in analysis.warnings"
      :key="'w' + i"
    >
      <div class="alert-body">{{ w }}</div>
    </b-alert>

    <b-row class="mb-1">
      <b-col md="8" class="d-flex align-items-center">
        <span class="text-muted">
          <strong>{{ analysis.row_count }}</strong> completed rows ·
          region <strong>{{ analysis.company.source }}</strong> matched to
          <strong>{{ analysis.company.title }}</strong>
        </span>
      </b-col>
      <b-col md="4" class="d-flex justify-content-end">
        <b-form-checkbox v-model="onlyUnresolved" switch>
          Show only items needing attention
        </b-form-checkbox>
      </b-col>
    </b-row>

    <b-tabs pills>
      <!-- ── Service areas ───────────────────────────────────────────── -->
      <b-tab>
        <template #title>
          Service Areas
          <b-badge :variant="areaBadge" class="ml-50">{{ areaUnresolved }}</b-badge>
        </template>

        <p class="text-muted mt-1">
          A service area usually covers several sites, so pick every site it
          includes. Your choices are remembered for future imports.
        </p>

        <b-table
          small
          striped
          bordered
          responsive
          :items="visibleAreas"
          :fields="areaFields"
          show-empty
          empty-text="Nothing to resolve here"
        >
          <template #cell(service_area)="{ item }">
            <span v-if="item.service_area">{{ item.service_area }}</span>
            <span v-else class="text-muted font-italic">(blank)</span>
            <b-badge variant="light-secondary" class="ml-50">{{ item.row_count }} rows</b-badge>
          </template>

          <template #cell(source)="{ item }">
            <b-badge :variant="sourceVariant(item.source)">{{ sourceLabel(item.source) }}</b-badge>
          </template>

          <template #cell(sites)="{ item }">
            <v-select
              v-if="item.source !== 'no-site'"
              multiple
              :options="item.site_options"
              label="title"
              :reduce="(s) => s.id"
              v-model="serviceAreaMap[item.service_area]"
              placeholder="Select site(s)"
            />
            <span v-else class="text-muted font-italic">No site recorded</span>
          </template>
        </b-table>
      </b-tab>

      <!-- ── Courses ─────────────────────────────────────────────────── -->
      <b-tab>
        <template #title>
          Courses
          <b-badge :variant="courseBadge" class="ml-50">{{ courseUnresolved }}</b-badge>
        </template>

        <p class="text-muted mt-1">
          Link each spreadsheet course to a course in the system, or create a
          new CPD course from it.
        </p>

        <b-table
          small
          striped
          bordered
          responsive
          :items="visibleCourses"
          :fields="courseFields"
          show-empty
          empty-text="Nothing to resolve here"
        >
          <template #cell(source_name)="{ item }">
            <div>{{ item.source_name }}</div>
            <b-badge variant="light-secondary">{{ item.row_count }} rows</b-badge>
            <b-badge variant="light-secondary" class="ml-25" v-if="item.cpd_credits !== null">
              {{ item.cpd_credits }} credits
            </b-badge>
          </template>

          <template #cell(source)="{ item }">
            <b-badge :variant="sourceVariant(item.source)">{{ sourceLabel(item.source) }}</b-badge>
            <div v-if="topScore(item)" class="mt-25">
              <small :class="scoreClass(topScore(item))">{{ topScore(item) }}% match</small>
            </div>
          </template>

          <template #cell(decision)="{ item }">
            <div class="d-flex align-items-center">
              <b-form-radio-group
                buttons
                button-variant="outline-primary"
                size="sm"
                class="mr-1"
                :options="courseActions"
                v-model="courseMap[item.source_name].action"
              />
              <v-select
                v-if="courseMap[item.source_name].action === 'link'"
                class="flex-grow-1"
                :options="courseOptions"
                label="title"
                :reduce="(c) => c.id"
                v-model="courseMap[item.source_name].course_id"
                placeholder="Select a course"
              />
              <span v-else class="text-muted">
                Creates <strong>{{ item.source_name }}</strong> as a new CPD course
              </span>
            </div>
          </template>
        </b-table>
      </b-tab>

      <!-- ── People ──────────────────────────────────────────────────── -->
      <b-tab>
        <template #title>
          People
          <b-badge :variant="peopleBadge" class="ml-50">{{ peopleUnresolved }}</b-badge>
        </template>

        <p class="text-muted mt-1">
          The spreadsheet has no email column, so people are matched by name.
          Check any suggestion below before confirming — creating a user needs
          an email address.
        </p>

        <b-table
          small
          striped
          bordered
          responsive
          :items="visiblePeople"
          :fields="peopleFields"
          show-empty
          empty-text="Nothing to resolve here"
        >
          <template #cell(source_name)="{ item }">
            <div>{{ item.source_name }}</div>
            <b-badge variant="light-secondary">{{ item.row_count }} rows</b-badge>
            <div v-if="item.cross_company" class="mt-25">
              <b-badge variant="light-danger">
                Same name in another region: {{ item.cross_company.label }}
              </b-badge>
            </div>
          </template>

          <template #cell(source)="{ item }">
            <b-badge :variant="sourceVariant(item.source)">{{ sourceLabel(item.source) }}</b-badge>
            <div v-if="topScore(item)" class="mt-25">
              <small :class="scoreClass(topScore(item))">{{ topScore(item) }}% match</small>
            </div>
          </template>

          <template #cell(decision)="{ item }">
            <div class="d-flex align-items-center">
              <b-form-radio-group
                buttons
                button-variant="outline-primary"
                size="sm"
                class="mr-1"
                :options="userActions"
                v-model="userMap[item.source_name].action"
              />

              <v-select
                v-if="userMap[item.source_name].action === 'link'"
                class="flex-grow-1"
                :options="userOptions"
                label="label"
                :reduce="(u) => u.id"
                v-model="userMap[item.source_name].user_id"
                placeholder="Select a user"
              />

              <b-form-input
                v-else-if="userMap[item.source_name].action === 'create'"
                class="flex-grow-1"
                type="email"
                placeholder="email@citinfo.ie"
                v-model.trim="userMap[item.source_name].email"
              />

              <span v-else class="text-muted">
                {{ item.row_count }} row{{ item.row_count === 1 ? "" : "s" }} will be skipped
              </span>
            </div>
          </template>
        </b-table>
      </b-tab>
    </b-tabs>

    <div class="mt-2 d-flex align-items-center">
      <b-button variant="outline-secondary" @click="$emit('cancel')">Cancel</b-button>
      <b-button
        variant="primary"
        class="ml-1"
        :disabled="unresolvedCount > 0"
        @click="confirm"
      >
        Import {{ analysis.row_count }} completions
      </b-button>
      <span v-if="unresolvedCount" class="text-muted ml-1">
        Resolve the remaining {{ unresolvedCount }} item{{ unresolvedCount === 1 ? "" : "s" }} first.
      </span>
    </div>
  </div>
</template>

<script>
import {
  BAlert,
  BRow,
  BCol,
  BTabs,
  BTab,
  BTable,
  BBadge,
  BButton,
  BFormInput,
  BFormCheckbox,
  BFormRadioGroup,
} from "bootstrap-vue";
import vSelect from "vue-select";

export default {
  components: {
    BAlert,
    BRow,
    BCol,
    BTabs,
    BTab,
    BTable,
    BBadge,
    BButton,
    BFormInput,
    BFormCheckbox,
    BFormRadioGroup,
    vSelect,
  },
  props: {
    analysis: { type: Object, required: true },
    courseOptions: { type: Array, default: () => [] },
    userOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      onlyUnresolved: false,
      serviceAreaMap: {},
      courseMap: {},
      userMap: {},
      courseActions: [
        { text: "Link", value: "link" },
        { text: "Create", value: "create" },
      ],
      userActions: [
        { text: "Link", value: "link" },
        { text: "Create", value: "create" },
        { text: "Skip", value: "skip" },
      ],
      areaFields: [
        { key: "service_area", label: "Service area", thStyle: "width:22%" },
        { key: "source", label: "Match", thStyle: "width:16%" },
        { key: "sites", label: "Sites in the system" },
      ],
      courseFields: [
        { key: "source_name", label: "Spreadsheet course", thStyle: "width:32%" },
        { key: "source", label: "Match", thStyle: "width:14%" },
        { key: "decision", label: "Maps to" },
      ],
      peopleFields: [
        { key: "source_name", label: "Spreadsheet name", thStyle: "width:26%" },
        { key: "source", label: "Match", thStyle: "width:14%" },
        { key: "decision", label: "Maps to" },
      ],
    };
  },
  created() {
    this.seedDecisions();
  },
  computed: {
    visibleAreas() {
      return this.onlyUnresolved
        ? this.analysis.service_areas.filter((a) => !this.areaResolved(a))
        : this.analysis.service_areas;
    },
    visibleCourses() {
      return this.onlyUnresolved
        ? this.analysis.courses.filter((c) => !this.courseResolved(c))
        : this.analysis.courses;
    },
    visiblePeople() {
      return this.onlyUnresolved
        ? this.analysis.people.filter((p) => !this.personResolved(p))
        : this.analysis.people;
    },
    areaUnresolved() {
      return this.analysis.service_areas.filter((a) => !this.areaResolved(a)).length;
    },
    courseUnresolved() {
      return this.analysis.courses.filter((c) => !this.courseResolved(c)).length;
    },
    peopleUnresolved() {
      return this.analysis.people.filter((p) => !this.personResolved(p)).length;
    },
    unresolvedCount() {
      return this.areaUnresolved + this.courseUnresolved + this.peopleUnresolved;
    },
    areaBadge() {
      return this.areaUnresolved ? "danger" : "success";
    },
    courseBadge() {
      return this.courseUnresolved ? "danger" : "success";
    },
    peopleBadge() {
      return this.peopleUnresolved ? "danger" : "success";
    },
  },
  methods: {
    // Seed every row with the server's suggestion so a reviewer who agrees
    // with all of them only has to press Confirm.
    seedDecisions() {
      const areas = {};
      this.analysis.service_areas.forEach((a) => {
        areas[a.service_area] = [...(a.suggested_site_ids || [])];
      });
      this.serviceAreaMap = areas;

      const courses = {};
      this.analysis.courses.forEach((c) => {
        courses[c.source_name] = { action: c.action, course_id: c.course_id };
      });
      this.courseMap = courses;

      const users = {};
      this.analysis.people.forEach((p) => {
        users[p.source_name] = {
          action: p.action,
          user_id: p.user_id,
          email: p.suggested_email,
          first: p.first,
          last: p.last,
        };
      });
      this.userMap = users;
    },

    areaResolved(area) {
      if (area.source === "no-site") return true;
      return (this.serviceAreaMap[area.service_area] || []).length > 0;
    },
    courseResolved(course) {
      const d = this.courseMap[course.source_name];
      if (!d) return false;
      return d.action === "create" || !!d.course_id;
    },
    personResolved(person) {
      const d = this.userMap[person.source_name];
      if (!d) return false;
      if (d.action === "skip") return true;
      if (d.action === "link") return !!d.user_id;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email || "");
    },

    topScore(item) {
      return item.matches && item.matches.length ? item.matches[0].score : null;
    },
    scoreClass(score) {
      if (score >= 90) return "text-success";
      if (score >= 75) return "text-warning";
      return "text-danger";
    },
    sourceVariant(source) {
      return {
        exact: "light-success",
        "saved-mapping": "light-primary",
        fuzzy: "light-warning",
        "no-site": "light-secondary",
        unresolved: "light-danger",
      }[source] || "light-secondary";
    },
    sourceLabel(source) {
      return {
        exact: "Exact",
        "saved-mapping": "Remembered",
        fuzzy: "Suggested",
        "no-site": "No site",
        unresolved: "Needs review",
      }[source] || source;
    },

    confirm() {
      // Strip service areas the reviewer left empty so the server does not
      // persist a mapping to nothing.
      const areas = {};
      Object.keys(this.serviceAreaMap).forEach((k) => {
        if (k && (this.serviceAreaMap[k] || []).length) {
          areas[k] = this.serviceAreaMap[k];
        }
      });

      this.$emit("confirm", {
        token: this.analysis.token,
        company_id: this.analysis.company.id,
        service_area_map: areas,
        course_map: this.courseMap,
        user_map: this.userMap,
      });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
